import { gql } from '@apollo/client';

/**
 * GraphQL query til at hente den nuværende bruger (den loggede ind bruger)
 * @returns Brugerens oplysninger (id, email, navn, role, lastLogin, createdAt, updatedAt)
 */
export const ME = gql`
  query Me {
    me {
      id
      email
      name
      role
      pageAccess
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

/**
 * GraphQL query til at hente en specifik mate efter ID
 * @param id - ID af den mate der skal hentes
 * @returns Mate oplysninger (id, email, navn, role, lastLogin, createdAt, updatedAt)
 */
export const MATE = gql`
  query Mate($id: ID!) {
    mate(id: $id) {
      id
      email
      name
      role
      pageAccess
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

/**
 * GraphQL query til at hente en mate efter email (kun admin)
 * @param email - Email af den mate der skal hentes
 * @returns Mate oplysninger (id, email, navn, role, lastLogin, createdAt, updatedAt)
 */
export const MATE_BY_EMAIL = gql`
  query MateByEmail($email: String!) {
    mateByEmail(email: $email) {
      id
      email
      name
      role
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

/**
 * GraphQL query til at hente alle brugere (kun admin)
 * @returns Array af alle brugere i systemet
 */
export const ALL_USERS = gql`
  query AllUsers {
    allUsers {
      id
      email
      name
      role
      pageAccess
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

export const VALIDATE_SIGNUP_LINK = gql`
  query ValidateSignupLink($token: String!) {
    validateSignupLink(token: $token) {
      id
      token
      isUsed
      usedBy
      createdBy
      createdAt
      expiresAt
      updatedAt
      isValid
    }
  }
`;

export const MY_SIGNUP_LINKS = gql`
  query MySignupLinks {
    mySignupLinks {
      id
      token
      isUsed
      usedBy
      createdBy
      createdAt
      expiresAt
      updatedAt
      isValid
    }
  }
`;

export const VALIDATE_PASSWORD_RESET_LINK = gql`
  query ValidatePasswordResetLink($token: String!) {
    validatePasswordResetLink(token: $token) {
      id
      token
      isUsed
      usedBy
      mateId
      createdBy
      createdAt
      expiresAt
      updatedAt
      isValid
    }
  }
`;

export const MY_PASSWORD_RESET_LINKS = gql`
  query MyPasswordResetLinks {
    myPasswordResetLinks {
      id
      token
      isUsed
      usedBy
      mateId
      createdBy
      createdAt
      expiresAt
      updatedAt
      isValid
    }
  }
`;

export const CHAMPIONS_LEAGUE_SETTINGS = gql`
  query ChampionsLeagueSettings {
    championsLeagueSettings {
      id
      phaseLocks {
        phase0
        phase1
        phase2
        phase2Playoff
        phase2Round16
        phase2Quarters
        phase2Semis
        phase3
      }
      realResults {
        leagueStanding
        playoffResults {
          id
          home
          away
          winner
          homeScore
          awayScore
        }
        roundOf16Results {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
        quarterFinalResults {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
        semiFinalResults {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
        finalResult {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const MY_CHAMPIONS_LEAGUE_GAME_DATA = gql`
  query MyChampionsLeagueGameData {
    myChampionsLeagueGameData {
      id
      mateId
      mateName
      preseasonWinner
      leagueStanding
      playoffPredictions
      knockoutPredictions
      finalPrediction
      points
      phase0Points
      phase1Points
      phase2Points
      phase3Points
      createdAt
      updatedAt
    }
  }
`;

export const ALL_CHAMPIONS_LEAGUE_GAME_DATA = gql`
  query AllChampionsLeagueGameData {
    allChampionsLeagueGameData {
      id
      mateId
      mateName
      preseasonWinner
      leagueStanding
      playoffPredictions
      knockoutPredictions
      finalPrediction
      points
      phase0Points
      phase1Points
      phase2Points
      phase3Points
      createdAt
      updatedAt
    }
  }
`;

export const CHAMPIONS_LEAGUE_TEAMS = gql`
  query ChampionsLeagueTeams {
    championsLeagueTeams {
      id
      teams {
        name
        points
        position
      }
      season
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const ALL_CHAMPIONS_LEAGUE_TEAMS = gql`
  query AllChampionsLeagueTeams {
    allChampionsLeagueTeams {
      id
      teams {
        name
        points
        position
      }
      season
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const LANDSKAMP_MATCHES = gql`
  query LandskampMatches {
    landskampMatches {
      id
      kickoff
      homeTeam
      awayTeam
      competition
      venue
      city
      homeScore
      awayScore
      createdAt
      updatedAt
    }
  }
`;

export const LANDSKAMP_GUESSES = gql`
  query LandskampGuesses($matchId: ID!) {
    landskampGuesses(matchId: $matchId) {
      id
      matchId
      mateId
      mateName
      homeScore
      awayScore
      createdAt
      updatedAt
    }
  }
`;

export const KAMPDAG_MATCHES = gql`
  query KampdagMatches {
    kampdagMatches {
      id
      title
      kickoff
      homeTeam
      awayTeam
      venue
      city
      enableResult
      enableYellowCards
      enableRedCards
      enableCorners
      homeScore
      awayScore
      yellowCards
      redCards
      corners
      createdAt
      updatedAt
    }
  }
`;

export const KAMPDAG_GUESSES = gql`
  query KampdagGuesses($matchId: ID!) {
    kampdagGuesses(matchId: $matchId) {
      id
      matchId
      mateId
      mateName
      homeScore
      awayScore
      yellowCards
      redCards
      corners
      createdAt
      updatedAt
    }
  }
`;

export const VAPID_PUBLIC_KEY = gql`
  query VapidPublicKey {
    vapidPublicKey
  }
`;

export const WORLD_CUP_MATCHES = gql`
  query WorldCupMatches {
    worldCupMatches {
      id
      homeTeam
      awayTeam
      kickoffTime
      homeGoals
      awayGoals
      status
      stage
      sortOrder
    }
  }
`;

export const MY_WORLD_CUP_GUESSES = gql`
  query MyWorldCupGuesses {
    myWorldCupGuesses {
      id
      userId
      userName
      matchId
      homeGoals
      awayGoals
      isJoker
      pointsAwarded
      createdAt
    }
  }
`;

export const WORLD_CUP_LEADERBOARD = gql`
  query WorldCupLeaderboard {
    worldCupLeaderboard {
      rank
      userId
      userName
      totalPoints
      perfectPredictions
      jokerPoints
      worldChampionPoints
      championTeam
    }
  }
`;

export const WORLD_CUP_LEAGUES = gql`
  query WorldCupLeagues {
    worldCupLeagues {
      id
      name
      ownerId
      inviteCode
      createdAt
      leaderboard {
        rank
        userId
        userName
        totalPoints
        perfectPredictions
        jokerPoints
        worldChampionPoints
        championTeam
      }
    }
  }
`;

export const MY_WORLD_CHAMPION_GUESS = gql`
  query MyWorldChampionGuess {
    myWorldChampionGuess {
      id
      userId
      championTeam
      pointsAwarded
      locked
      updatedAt
    }
  }
`;

export const WORLD_CUP_MATCH_GUESSES = gql`
  query WorldCupMatchGuesses($matchId: ID!) {
    worldCupMatchGuesses(matchId: $matchId) {
      id
      userId
      userName
      matchId
      homeGoals
      awayGoals
      isJoker
      pointsAwarded
      createdAt
    }
  }
`;

export const WORLD_CUP_TEAMS = gql`
  query WorldCupTeams {
    worldCupTeams {
      id
      teams {
        name
        group
        flagCode
      }
      updatedAt
    }
  }
`;

export const WORLD_CUP_STAGES = gql`
  query WorldCupStages {
    worldCupStages {
      name
      sortOrder
    }
  }
`;

export const FODBOLDHOLD_TEAM_FIELDS = gql`
  fragment FodboldholdTeamFields on FodboldholdTeam {
    id
    name
    initials
    logo
    background
    mobilePay
    fineBoxId
    icalUrl
    inviteCode
    members {
      mateId
      name
      isAdmin
      fineChair
      inFineBox
    }
    fineTypes {
      id
      text
      amount
    }
    news {
      id
      text
      path
      createdAt
    }
    posts {
      id
      authorId
      authorName
      text
      image
      createdAt
      likedBy
      comments {
        id
        authorId
        authorName
        text
        createdAt
      }
    }
    seasons {
      id
      name
      current
      feeAmount
      feeDue
      standingsUrl
      paidMateIds
      matches {
        id
        seasonId
        externalId
        opponent
        home
        kickoff
        place
        meeting
        note
        max
        status
        manual
        attendees
        waitlist
        declined
        goals {
          playerId
          count
        }
        messages {
          id
          authorId
          authorName
          text
          image
          createdAt
          likedBy
          comments {
            id
            authorId
            authorName
            text
            createdAt
          }
        }
      }
      events {
        id
        seasonId
        title
        kickoff
        place
        description
        max
        attendees
        waitlist
        declined
        messages {
          id
          authorId
          authorName
          text
          image
          createdAt
          likedBy
          comments {
            id
            authorId
            authorName
            text
            createdAt
          }
        }
      }
      fines {
        id
        seasonId
        typeId
        mateId
        text
        amount
        matchId
        paid
        createdAt
      }
      finePayments {
        mateId
        amount
      }
      fineClaims {
        id
        mateId
        amount
        createdAt
      }
      fineApprovals {
        id
        mateId
        amount
        approvedBy
        approvedByName
        kind
        createdAt
      }
    }
  }
`;

export const MY_FODBOLDHOLD = gql`
  query MyFodboldhold {
    myFodboldhold {
      ...FodboldholdTeamFields
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const FODBOLDHOLD_INVITES = gql`
  query FodboldholdInvites($teamId: ID!) {
    fodboldholdInvites(teamId: $teamId) {
      id
      token
      expiresAt
      maxUses
      usedCount
      active
      createdAt
    }
  }
`;

export const FODBOLDHOLD_INVITE = gql`
  query FodboldholdInvite($token: String!) {
    fodboldholdInvite(token: $token) {
      valid
      teamName
      teamId
    }
  }
`;

export const ADMIN_FODBOLDHOLD_ACCESS = gql`
  query AdminFodboldholdAccess {
    allFodboldhold {
      id
      name
      members {
        mateId
        isAdmin
      }
    }
  }
`;

export const ALL_FODBOLDHOLD = gql`
  query AllFodboldhold {
    allFodboldhold {
      ...FodboldholdTeamFields
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const WORLD_CUP_MISSING_GUESS_REMINDERS = gql`
  query WorldCupMissingGuessReminders($hours: Int) {
    worldCupMissingGuessReminders(hours: $hours) {
      hours
      matches {
        id
        homeTeam
        awayTeam
        kickoffTime
        stage
      }
      mates {
        userId
        userName
        email
        missingCount
        missingMatchIds
      }
    }
  }
`;
